import React, { useState } from 'react'
import {Link, useParams, useNavigate} from 'react-router-dom'
import {
    Container,
    Typography,
    Box,
    Grid,
    Button,
    alpha,
    Fade,
    Chip,
    Paper, Divider, IconButton
} from '@mui/material'
import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Schedule,
  Star,
  TrendingUp,
    AutoAwesome,
    DesignServices,
    Lightbulb,
    ShoppingBag,
    Brush
} from '@mui/icons-material'

const ServicesPage = () => {
  const { serviceId } = useParams()
    const navigate = useNavigate()
    const [isVisible, setIsVisible] = useState(false)

  React.useEffect(() => {
    setIsVisible(true)
  }, [serviceId])

  const services = {
    'luxury-home-staging': {
      id: 'luxury-home-staging',
      icon: <DesignServices sx={{ fontSize: 48 }} />,
        title: 'Home Staging',
        tagline: 'Transforming listings into compelling narratives',
        gradient: 'linear-gradient(to bottom, #FBFAF8 0%, #EFEAE4 100%)',
        color: '#614f33',
        description: 'Transform properties into irresistible homes that tell a story and create emotional connections with potential buyers.',
        overview: 'Our Home Staging service goes beyond simple decoration. We create aspirational lifestyles within your property, helping buyers envision themselves living their best life in the space. Through strategic furniture placement, curated accessories, and thoughtful styling, we maximize your property\'s potential and accelerate sales.',
      whatItEntails: [
        'Comprehensive property assessment and strategy development',
        'Custom furniture selection and placement optimization',
        'Art and accessory curation to enhance spatial flow',
        'Professional photography preparation and styling',
        'Virtual staging options for vacant properties'
      ],
      process: [
          {
              label: 'Discovery',
              description: 'In-depth property analysis, understanding your target market and developing a staging strategy.'
          },
          {
              label: 'Design',
              description: 'Customized staging plan, selecting furniture and accessories that complement your property.'
          },
          {
              label: 'Transform',
              description: 'Professional staging creating inviting spaces that feel both luxurious and livable.'
          },
          {
              label: 'Refine',
              description: 'Final touches that make your property feel like home - from flowers to styled details.'
          }
      ],
      benefits: [
        'Faster sale times - typically 73% quicker than unstaged homes',
        'Higher offers - staged homes sell for 6-10% more on average',
          'Professional photography that captures best angles',
        'Virtual tour creation for digital marketing',
        'Competitive edge in the luxury market'
      ],
        timeline: '2-5 days',
      idealFor: ['Luxury property sellers', 'Real estate developers', 'Vacation home owners', 'Estate sales']
    },
    'interior-design-styling': {
      id: 'interior-design-styling',
      icon: <AutoAwesome sx={{ fontSize: 48 }} />,
      title: 'Interior Design & Styling',
        tagline: 'Creating personalized, functional spaces',
        gradient: 'linear-gradient(70deg, #F9F7F4 0%, #E5DDD4 100%)',
        color: '#8B7355',
        description: 'Craft bespoke interiors that reflect your personality while enhancing your daily life through thoughtful design and impeccable styling.',
      overview: 'Our interior design service is a deeply personal journey where we translate your dreams and lifestyle into a beautifully functional reality. We blend aesthetics with practicality, creating spaces that not only look stunning but work perfectly for how you live.',
      whatItEntails: [
        'Comprehensive space planning and furniture layout',
        'Custom color palette and material selection',
        'Furniture, lighting, and accessory specification',
        'Window treatment design and fabrication',
        'Art curation and gallery wall design'
      ],
      process: [
          {
              label: 'Vision',
              description: 'Understanding your lifestyle, preferences, and aspirations through detailed conversations.'
          },
          {
              label: 'Concept',
              description: 'Comprehensive design concept including mood boards, floor plans, and material selections.'
          },
          {
              label: 'Develop',
              description: 'Refining concepts into detailed designs, selecting every element for cohesion and quality.'
          },
          {label: 'Implement', description: 'Managing the entire process, from procurement to installation.'}
      ],
      benefits: [
        'Spaces that truly reflect your personality and lifestyle',
        'Professional project management from concept to completion',
        'Access to trade-only resources and custom furnishings',
        'Increased property value through quality design',
        'Lifelong spaces that evolve with your needs'
      ],
        timeline: '6 weeks - 6 months',
      idealFor: ['Homeowners seeking transformation', 'New construction projects', 'Renovation guidance', 'Multi-room makeovers']
    },
    'space-consultations': {
      id: 'space-consultations',
      icon: <Lightbulb sx={{ fontSize: 48 }} />,
        title: 'Home Consultations',
        tagline: 'Expert guidance to unlock hidden potential',
        gradient: 'linear-gradient(to right, #F8F6F2 0%, #E7E0D9 100%)',
        color: '#4c4021',
      description: 'Get professional insights and actionable recommendations to maximize your space\'s functionality and aesthetic appeal.',
        overview: 'Our home consultations provide expert eyes on your current setup, offering creative solutions and practical advice to transform how you use and experience your space. Perfect for those who want professional guidance without full-service design.',
      whatItEntails: [
        'In-depth space analysis and measurement',
        'Furniture layout optimization strategies',
        'Storage and organization solutions',
        'Lighting and flow improvements',
        'Style enhancement recommendations'
      ],
      process: [
          {
              label: 'Assess',
              description: 'Thorough walkthrough of your space, discussing your challenges, goals, and vision.'
          },
          {
              label: 'Analyze',
              description: 'Analyzing the space\'s potential, identifying opportunities for improvement.'
          },
          {
              label: 'Recommend',
              description: 'Comprehensive report with specific recommendations, including layout suggestions.'
          },
          {label: 'Guide', description: 'Guidance on executing the recommendations, with optional follow-up support.'}
      ],
      benefits: [
        'Immediate actionable insights and solutions',
        'Cost-effective professional guidance',
        'Flexible implementation at your own pace',
        'Creative problem-solving for specific challenges',
        'Enhanced functionality without major investment'
      ],
        timeline: '2-4 hours',
      idealFor: ['DIY enthusiasts needing guidance', 'Small space challenges', 'Room layout dilemmas', 'Quick refresh ideas']
    },
    'personal-shopping': {
      id: 'personal-shopping',
      icon: <ShoppingBag sx={{ fontSize: 48 }} />,
      title: 'Personal Shopping',
        tagline: 'Curated decor sourcing with an expert eye',
        gradient: 'linear-gradient(120deg, #F4F2EF 0%, #DFD7CF 100%)',
        color: '#614f33',
      description: 'Let us handle the shopping while you enjoy the results. We source unique pieces that perfectly complement your space and style.',
      overview: 'Our personal shopping service takes the overwhelm out of finding the perfect pieces for your home. We leverage our network of suppliers and keen eye for quality to source items that elevate your space while saving you time and frustration.',
      whatItEntails: [
        'Personalized shopping list development',
        'Sourcing from trade-only showrooms and artisans',
        'Quality assessment and value analysis',
        'Coordination of deliveries and returns',
        'Style coordination with existing pieces'
      ],
      process: [
          {label: 'Discover', description: 'Understanding your aesthetic preferences, budget, and functional needs.'},
          {label: 'Curate', description: 'Sourcing options from our network of trusted vendors.'},
          {label: 'Refine', description: 'You review our selections, provide feedback, and we refine the choices.'},
          {label: 'Deliver', description: 'Handling all purchasing, delivery coordination, and quality assurance.'}
      ],
      benefits: [
        'Access to exclusive trade-only resources',
        'Time savings - we handle the research and sourcing',
        'Expert quality assessment and value analysis',
        'Cohesive selections that work with your existing pieces',
        'Stress-free delivery and setup coordination'
      ],
        timeline: '2-6 weeks',
      idealFor: ['Busy professionals', 'Those overwhelmed by choices', 'Custom furniture sourcing', 'Art and accessory curation']
    },
    'digital-design': {
      id: 'digital-design',
      icon: <Brush sx={{ fontSize: 48 }} />,
      title: 'Digital Design & Branding',
        tagline: 'Visual identities that tell your story',
        gradient: 'linear-gradient(to right, #F1EEEA 0%, #E2DAD2 100%)',
        color: '#614f33',
        description: 'Create compelling digital presence and brand identities that capture your essence and connect with your audience.',
      overview: 'In today\'s digital world, your online presence is your first impression. We craft cohesive brand identities and digital experiences that tell your story authentically while driving engagement and building trust with your audience.',
      whatItEntails: [
        'Brand strategy and identity development',
        'Website design and user experience optimization',
        'Social media graphics and content templates',
        'Marketing collateral and presentation design',
        'Digital asset management and brand guidelines'
      ],
      process: [
          {
              label: 'Brand',
              description: 'Understanding your business, audience, and market position to develop strategic foundation.'
          },
          {
              label: 'Identity',
              description: 'Creating complete visual identity including logo, color palette, and typography.'
          },
          {label: 'Digital', description: 'Bringing your brand to life across digital platforms with consistency.'},
          {label: 'Manage', description: 'Providing tools and guidelines to maintain brand consistency as you grow.'}
      ],
      benefits: [
        'Cohesive brand identity across all touchpoints',
        'Professional digital presence that builds trust',
        'Increased engagement and conversion rates',
        'Scalable design systems for future growth',
        'Competitive edge in your market'
      ],
        timeline: '4-12 weeks',
        idealFor: ['Small businesses and startups', 'Personal brands and influencers', 'Service-based businesses', 'Brand refreshes']
    }
  }

  const currentService = services[serviceId]

  if (!currentService) {
    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#FDFDFB'
        }}>
            <Container>
                <Typography variant="h4" sx={{textAlign: 'center', color: '#666', mb: 3}}>Service not found</Typography>
                <Box sx={{textAlign: 'center'}}>
                    <Button onClick={() => navigate('/services')} variant="outlined"
                            sx={{borderColor: '#8C7B6B', color: '#8C7B6B'}}>
                        View All Services
                    </Button>
                </Box>
            </Container>
        </Box>
    )
  }

  return (
      <Box sx={{bgcolor: '#FDFDFB', minHeight: '100vh'}}>
          <Box sx={{
              background: currentService.gradient,
              py: {xs: 5, md: 9},
              position: 'relative',
              overflow: 'hidden',

          }}>
              <Container maxWidth="xl" sx={{position: 'relative', zIndex: 1}}>
                  <Box>


                      <Box
                          onClick={() => navigate('/services')}
                          sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 1,
                              mt: 5,
                              color: '#574732',
                              border: '1px solid #675944',
                              borderRadius: '50px',
                              px: 2,
                              py: 1.5,
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              letterSpacing: '0.02em',
                              transition: 'all 0.3s ease',
                              backdropFilter: 'blur(10px)',
                              fontFamily: '"Inter", sans-serif',
                              cursor: 'pointer',
                              userSelect: 'none',
                              '&:hover': {
                                  backgroundColor: 'rgba(103, 89, 68, 0.08)',
                                  borderColor: '#574732',
                                  boxShadow: '0 4px 12px rgba(103, 89, 68, 0.2)',
                                  letterSpacing: '0.03em'
                              },
                              '& svg, & *': {
                                  pointerEvents: 'none'
                              }
                          }}
                      >
                          <ArrowBack sx={{fontSize: '1.25rem'}}/>
                          All Services
                      </Box>

                  </Box>

                  <Fade in={isVisible} timeout={800}>
                      <Box>
                          <Typography
                              variant="h1"
                              sx={{
                                  fontSize: {xs: '2.75rem', md: '4.2rem'},
                                  fontWeight: 300,
                                  textAlign: 'center',
                                  fontFamily: '"Cormorant Garamond", serif',
                                  lineHeight: 1.1,
                                  letterSpacing: '0.02em',
                                  mb: 2
                              }}
                          >
                              {currentService.title}
                          </Typography>

            </Box>
                  </Fade>
              </Container>
          </Box>

          <Container maxWidth="lg" sx={{py: {xs: 6, md: 10}}}>
              <Grid container spacing={6}>
                  <Grid item xs={12} md={8}>
            <Fade in={isVisible} timeout={1000}>
                <Box sx={{mb: 8}}>
                <Typography
                  variant="h3"
                  sx={{
                      fontSize: {xs: '1.75rem', md: '2.25rem'},
                    fontWeight: 300,
                      fontFamily: '"Cormorant Garamond", serif',
                    color: '#1a1a1a',
                      mb: 3
                  }}
                >
                    About This Service
                </Typography>
                    <Divider sx={{mb: 4}}/>
                    <Typography sx={{fontSize: '1.125rem', lineHeight: 1.8, color: '#666', mb: 4}}>
                        {currentService.description}
                </Typography>
                    <Typography sx={{fontSize: '1.0625rem', lineHeight: 1.8, color: '#666'}}>
                  {currentService.overview}
                </Typography>
                </Box>
            </Fade>

                      <Fade in={isVisible} timeout={1200}>
                          <Box sx={{mb: 8}}>
                <Typography
                  variant="h4"
                  sx={{
                      fontSize: {xs: '1.5rem', md: '1.75rem'},
                      fontWeight: 400,
                    color: '#1a1a1a',
                      mb: 4
                  }}
                >
                    What's Included
                </Typography>
                              <Grid container spacing={2}>
                  {currentService.whatItEntails.map((item, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                          <Box sx={{display: 'flex', gap: 1.5, alignItems: 'flex-start'}}>
                              <CheckCircle sx={{color: currentService.color, fontSize: 22, mt: 0.2, flexShrink: 0}}/>
                              <Typography sx={{color: '#666', lineHeight: 1.6}}>{item}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                          </Box>
            </Fade>

                      <Fade in={isVisible} timeout={1400}>
                          <Box sx={{mb: 8}}>
                <Typography
                    variant="h4"
                  sx={{
                      fontSize: {xs: '1.5rem', md: '1.75rem'},
                      fontWeight: 400,
                      color: '#1a1a1a',
                      mb: 4
                  }}
                >
                    Our Process
                </Typography>
                              <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
                  {currentService.process.map((step, index) => (
                      <Paper
                          key={index}
                          elevation={0}
                          sx={{
                              p: 3,
                              border: `1px solid ${alpha(currentService.color, 0.15)}`,
                              borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            borderColor: currentService.color,
                            transform: 'translateX(4px)',
                            boxShadow: `0 4px 12px ${alpha(currentService.color, 0.1)}`
                        }
                          }}
                      >
                          <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start'}}>
                              <Box
                                  sx={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: '50%',
                                      bgcolor: alpha(currentService.color, 0.1),
                                      color: currentService.color,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      flexShrink: 0
                                  }}
                              >
                                  {index + 1}
                              </Box>
                              <Box>
                                  <Typography sx={{fontWeight: 600, color: '#1a1a1a', mb: 0.5, fontSize: '1.0625rem'}}>
                                      {step.label}
                                  </Typography>
                                  <Typography sx={{color: '#666', lineHeight: 1.6}}>
                                      {step.description}
                                  </Typography>
                              </Box>
                          </Box>
                      </Paper>
                  ))}
                              </Box>
                          </Box>
            </Fade>

                      <Fade in={isVisible} timeout={1600}>
                          <Box>
                <Typography
                    variant="h4"
                  sx={{
                      fontSize: {xs: '1.5rem', md: '1.75rem'},
                      fontWeight: 400,
                    color: '#1a1a1a',
                      mb: 4
                  }}
                >
                    Key Benefits
                </Typography>
                              <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.5}}>
                                  {currentService.benefits.map((benefit, index) => (
                                      <Box key={index} sx={{display: 'flex', gap: 2, alignItems: 'flex-start'}}>
                                          <Box
                                              sx={{
                                                  width: 8,
                                                  height: 8,
                                                  borderRadius: '50%',
                                                  bgcolor: currentService.color,
                                                  mt: 0.75,
                                                  flexShrink: 0
                                              }}
                                          />
                                          <Typography sx={{color: '#666', lineHeight: 1.6, fontSize: '1.0625rem'}}>
                                              {benefit}
                                          </Typography>
                                      </Box>
                                  ))}
                              </Box>
                          </Box>
            </Fade>
          </Grid>

                  <Grid item xs={12} md={4}>
                      <Fade in={isVisible} timeout={1800}>
                          <Box sx={{position: {md: 'sticky'}, top: {md: 100}}}>
                              <Paper
                                  elevation={0}
                                  sx={{
                                      p: 4,
                                      border: `1px solid ${alpha('#8C7B6B', 0.15)}`,
                                      borderRadius: 3,
                                      background: currentService.gradient,
                                      mb: 3
                                  }}
                              >
                                  <Typography variant="body1"
                                              sx={{textAlign: 'center', fontWeight: 600, color: '#1a1a1a', mb: 1}}>
                                      Ideal For
                                  </Typography>
                                  <Divider sx={{mb: 2}}/>
                                  <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5}}>
                                      {currentService.idealFor.map((item, index) => (
                                          <Chip
                                              key={index}
                                              label={item}

                                              sx={{
                                                  bgcolor: alpha(currentService.color, 0.02),
                                                  color: '#574732',
                                                  border: `1px solid ${alpha(currentService.color, 0.2)}`,
                                                  fontWeight: 500,
                                                  fontSize: '0.8125rem',
                                                  '&:hover': {
                                                      bgcolor: alpha(currentService.color, 0.08),
                                                      color: currentService.color,
                                                  }
                                              }}
                                          />
                                      ))}
                                  </Box>
                              </Paper>

                              <Paper
                                  elevation={0}
                                  sx={{
                                      p: 4,
                                      background: currentService.gradient,
                                      borderRadius: 3,
                                      position: 'relative',
                                      overflow: 'hidden',

                                  }}
                              >
                                  <Box sx={{position: 'relative', zIndex: 1}}>
                                      <Typography variant="h6" sx={{fontWeight: 500, mb: 2}}>
                                          Ready to Get Started?
                    </Typography>
                                      <Typography sx={{mb: 3, opacity: 1.5, color: '#121111', lineHeight: 1.6}}>
                                          Let's discuss your project and create something extraordinary together.
                    </Typography>
                    <Button
                      component={Link}
                      to="/contact"
                      variant="contained"
                      fullWidth
                      endIcon={<ArrowForward />}
                      sx={{
                          background: currentService.gradient,
                          border: '1px solid #574b3a',
                          color: currentService.color,
                          py: 1.5,
                          fontSize: '1rem',
                        fontWeight: 500,
                          textTransform: 'none',
                          borderRadius: 2,
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                          transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                        Start Your Project
                    </Button>
                                  </Box>
                              </Paper>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default ServicesPage
